import { useState } from "react";
import { useClientAuth } from "../lib/auth-context";
import { useClientRouter, ClientLink } from "../lib/router-context";
import {
  LayoutDashboard, Bot, ShoppingCart, FileText, BookOpen, Package, LifeBuoy, Building2,
  LogOut, ChevronLeft, ChevronRight,
} from "lucide-react";

function formatNumeroClient(n: number | null | undefined): string {
  if (!n) return "";
  return `JBT-CLI-${String(n).padStart(4, "0")}`;
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

function hexToHsl(value: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(value);
  if (!match) return "221 83% 53%";
  const hex = match[1];
  const red = parseInt(hex.slice(0, 2), 16) / 255;
  const green = parseInt(hex.slice(2, 4), 16) / 255;
  const blue = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(red, green, blue), min = Math.min(red, green, blue);
  let hue = 0;
  let saturation = 0;
  const lightness = (max + min) / 2;
  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case red: hue = (green - blue) / delta + (green < blue ? 6 : 0); break;
      case green: hue = (blue - red) / delta + 2; break;
      default: hue = (red - green) / delta + 4;
    }
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

interface ClientLayoutProps {
  children: React.ReactNode;
}

const NAV_ITEMS = [
  { label: "Tableau de bord", icon: LayoutDashboard, href: "/", active: true },
  { label: "Profil société", icon: Building2, href: "/societe" },
  { label: "Mes robots", icon: Bot, href: "/robots", disabled: true },
  { label: "Documents", icon: FileText, href: "/documents", disabled: true },
  { label: "Ressources", icon: BookOpen, href: "/ressources", disabled: true },
  { label: "Commandes", icon: ShoppingCart, href: "/commandes", disabled: true },
  { label: "Accessoires", icon: Package, href: "/accessoires", disabled: true },
  { label: "Tickets SAV", icon: LifeBuoy, href: "/tickets", disabled: true },
];

export function ClientLayout({ children }: ClientLayoutProps) {
  const { user, client, brand, logout } = useClientAuth();
  const { path } = useClientRouter();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (href: string) => {
    if (href === "/") return path === "/" || path === "";
    return path.startsWith(href);
  };

  const companyName = client?.nomCommercial || client?.companyName || "Client";
  const numeroClient = formatNumeroClient(client?.numeroClient ?? null);
  const primary = brand.couleurPrimaire || "#4e63f0";
  const secondary = brand.couleurSecondaire || "#2facf3";
  const gradient = `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
  const chromeStyle = {
    "--primary": hexToHsl(primary),
    "--ring": hexToHsl(primary),
  } as React.CSSProperties;

  return (
    <div className="flex h-screen overflow-hidden bg-background" style={chromeStyle}>
      {/* Sidebar */}
      <aside className={`flex flex-col shrink-0 border-r border-border/60 bg-card transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}>
        {/* Header logo */}
        <div className="flex items-center gap-3 px-3 border-b border-border/40 min-h-[64px]" style={{ background: gradient }}>
          {brand.logoUrl || client?.logoUrl ? (
            <img src={brand.logoUrl || client?.logoUrl || ""} alt={companyName} className="w-8 h-8 rounded-lg object-contain shrink-0 bg-white/20" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0 bg-white/20">
              {getInitials(companyName)}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate text-white">{companyName}</p>
              {numeroClient && <p className="text-xs text-white/70 truncate">{numeroClient}</p>}
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="ml-auto p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(item => {
            const active = !item.disabled && isActive(item.href);
            const disabled = !!item.disabled;

            if (disabled) {
              return (
                <div
                  key={item.href}
                  className={`flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-muted-foreground/50 cursor-not-allowed ${collapsed ? "justify-center" : ""}`}
                  title={collapsed ? `${item.label} — Bientôt disponible` : undefined}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && (
                    <span className="flex-1 truncate">{item.label}</span>
                  )}
                  {!collapsed && (
                    <span className="text-[10px] bg-secondary text-muted-foreground px-1.5 py-0.5 rounded-full whitespace-nowrap">Bientôt</span>
                  )}
                </div>
              );
            }

            return (
              <ClientLink
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                  active ? "text-white font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
                 style={active ? { background: gradient } : {}}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate flex-1">{item.label}</span>}
              </ClientLink>
            );
          })}
        </nav>

        {/* Footer user */}
        <div className="border-t border-border/40 p-3 space-y-2">
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-xs font-medium truncate text-foreground">{user?.firstName} {user?.lastName || user?.email}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={logout}
            className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? "Déconnexion" : undefined}
            data-testid="button-client-logout"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
